/**
 * Every static segment in `routes.tsx`'s route table. A segment not in this set
 * is an id or a token and is replaced with `:id` — deny by default. Follow-up,
 * invitation and signing tokens live in the path and are working access
 * credentials; classifying by character shape instead (the earlier
 * lowercase-and-hyphen heuristic) let through the nanoid that happens to draw
 * only lowercase letters, and a credential that leaks one time in a hundred
 * million still leaks. `routePattern.test.ts` reads `routes.tsx` and fails when a
 * new static route is missing from here.
 *
 * Own module so `analytics.ts` and `pageTiming.ts` can share it without a cycle.
 */
export const STATIC_SEGMENTS: ReadonlySet<string> = new Set([
  'accept-invite',
  'accept-invites',
  'admin',
  'all-referrals',
  'appointments',
  'baa',
  'book',
  'calls',
  'clinics',
  'details',
  'edit',
  'faxes',
  'faxes-sent',
  'followup',
  'forgot-password',
  'home',
  'inbound',
  'incoming-faxes',
  'login',
  'my-profile',
  'new',
  'notifications',
  'patients',
  'pending',
  'profile',
  'provider-onboarding',
  'records-request-onboarding',
  'records-requests',
  'referrals',
  'register',
  'requests',
  'reset-password',
  'send',
  'settings',
  'sign',
  'sign-in',
  'staff',
  'verify-email',
  'view',
  'visit-followup',
  'your-health',
]);

export const routePattern = (pathname: string): string =>
  `/${pathname
    .split('/')
    .filter(Boolean)
    .map(s => (STATIC_SEGMENTS.has(s) ? s : ':id'))
    .join('/')}`;

/**
 * Scrubs one URL-valued analytics property.
 *
 * Only our own origin gets the route-pattern treatment: an external referrer
 * (`expectedhealthcare.com/imaging-center/mri-center-2/`) carries no token of
 * ours, and mapping its slug to `:id` would destroy the clinic-page attribution
 * the booking rollout is read by. External http(s) URLs lose query and hash
 * only. Anything that is not a URL or a path — PostHog's `$direct` sentinel,
 * `android-app://` and `about:blank` referrers — passes through untouched.
 */
export const scrubUrl = (value: string): string => {
  if (value.startsWith('/')) return routePattern(value.split(/[?#]/)[0]);
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return value;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return value;
  const path =
    url.origin === window.location.origin
      ? routePattern(url.pathname)
      : url.pathname;
  return `${url.origin}${path}`;
};
